// Per-service case studies — short cards designed for the inline section on
// each /services/:slug page. Two studies per service. Kept intentionally
// short (~40-60 words per "outcome") so the page stays scannable.

export const CASE_STUDIES = {
  "alta-nsps-surveys": [
    {
      title: "5-property refi portfolio · Atlanta MSA",
      client: "Regional title agent",
      challenge: "Five Class-A retail centers, parallel closings, 10-day window. Two parcels had unrecorded easements buried in Schedule B-II.",
      outcome: "All 5 sealed ALTAs delivered in 9 business days. We plotted every B-II exception, flagged 3 encroachments before closing, and zero post-closing corrections were issued.",
      metrics: ["5 ALTAs / 9 days", "0 corrections", "Table-A items 1–16"],
    },
    {
      title: "Hotel acquisition · downtown Nashville",
      client: "Hospitality investor (USA)",
      challenge: "Mid-block urban hotel with a shared loading easement, a vacated alley, and ambiguous airspace rights on the north wall.",
      outcome: "Air-rights diagrammed in 3D, easement matrix attached to the survey, lender-grade ALTA delivered 6 days from receipt of field data — survey audit passed first pass.",
      metrics: ["6-day turnaround", "Air-rights plotted", "1st-pass audit"],
    },
  ],

  "boundary-surveys": [
    {
      title: "100-acre rural retracement · East Texas",
      client: "Family trust + adjoiner",
      challenge: "1872 PLSS-original tract, multiple senior/junior conflicts, three sets of found monuments disagreeing by up to 14 ft.",
      outcome: "Senior-rights analysis with hierarchy-of-evidence memo, adjusted boundary closed to 1:10,000, and a clean monument-set schedule for the surveyor of record.",
      metrics: ["100 ac retraced", "1:10,000 closure", "Senior rights memo"],
    },
    {
      title: "Fence-line dispute · Calgary suburb",
      client: "Residential survey firm (Canada)",
      challenge: "Neighbour's pool encroaching on rear lot line; both parties needed an evidence-based plan before mediation.",
      outcome: "Boundary retracement plotted against the recorded plan of subdivision, encroachment quantified at 1.42 m², and a courtroom-ready exhibit package delivered in 48 hours.",
      metrics: ["48-hr exhibit", "1.42 m² encroachment", "Mediation-ready"],
    },
  ],

  "topographic-surveys": [
    {
      title: "Drone topo · 65-ac master-planned community",
      client: "Civil engineering firm (FL)",
      challenge: "Client supplied a 65-ac UAV point cloud (LAS) but needed a designer-ready 1-ft contour DWG with break-lines, utility ties, and a LandXML surface — in 3 days.",
      outcome: "Ground-classified the cloud, built TIN, generated 1-ft contours and break-lines, overlaid one-call utilities, delivered DWG + sealed PDF + LandXML on day 3.",
      metrics: ["65 ac / 3 days", "1-ft contours", "LandXML surface"],
    },
    {
      title: "LiDAR + total-station hybrid · Adelaide highway",
      client: "Civil consultancy (Australia)",
      challenge: "Mobile-LiDAR scan of a 4 km road corridor combined with ground total-station shots on the verge. Required RailCorp-style level structure and metric output.",
      outcome: "Cleaned the point cloud, fused with ground shots into one TIN, drafted to client's MicroStation level standard with metric annotation. Used downstream for pavement-rehab design.",
      metrics: ["4 km corridor", "Hybrid TIN", "MicroStation native"],
    },
  ],

  "subdivision-plats": [
    {
      title: "42-lot residential plat · Charlotte, NC",
      client: "Land developer",
      challenge: "Preliminary-plat conditions of approval required 7 design revisions across drainage, ROW radii, and HOA-tract reshaping in 4 weeks.",
      outcome: "Each revision turned in <48 hr with updated curve/line tables and closure reports. Final plat recorded on first county submittal, no resubmission fees.",
      metrics: ["42 lots", "7 revisions / 4 wk", "Recorded 1st try"],
    },
    {
      title: "Commercial re-plat & easement vacation · Mesa, AZ",
      client: "Industrial REIT",
      challenge: "Consolidation of 4 commercial parcels with a city-vacated utility easement and a relocated cross-access easement — all on one recordable instrument.",
      outcome: "Single re-plat carried both the vacation language and the new easement, approved by city planning and recorded without amendment. Saved client a separate easement-of-record filing.",
      metrics: ["4 → 1 parcel", "Vacation + new easement", "1 filing instead of 2"],
    },
  ],

  "construction-staking": [
    {
      title: "Mid-rise foundation · downtown Denver",
      client: "Mountain-region survey firm",
      challenge: "Foundation pour scheduled for Monday; designer issued a column-grid revision Friday afternoon. Crew needed updated stake-out by 6 a.m. Monday.",
      outcome: "New layout calc, cut-sheets, and Trimble data-collector file delivered Saturday 11 a.m. — crew set anchor bolts on schedule, no pour delay.",
      metrics: ["Weekend turnaround", "Anchor bolts on-time", "Trimble TFL file"],
    },
    {
      title: "Utility stake-out · 1.2 mi sanitary extension",
      client: "Heavy-civil contractor",
      challenge: "Storm and sanitary stake-out on a tight ROW with active gas and fiber utilities — needed conflict review before staking.",
      outcome: "We overlaid the design with the one-call ticket data, flagged 4 conflicts, re-staked one structure offset, and delivered cut-sheets that the field used unchanged through completion.",
      metrics: ["4 conflicts flagged", "1.2 mi staked", "Zero rework"],
    },
  ],

  "as-built-surveys": [
    {
      title: "Hospital expansion closeout · Vancouver, BC",
      client: "Health-authority owner (Canada)",
      challenge: "Three-year hospital expansion needed record drawings that merged design, RFI changes, and field as-built dimensions on a single set of MEP sheets.",
      outcome: "We layered design vs as-built in a colour-coded set, called out every dimensional variance >50 mm, and packaged the closeout for owner's CMMS handover.",
      metrics: ["CMMS-ready", ">50 mm variance flagged", "Phased delivery"],
    },
    {
      title: "Retail centre site as-built · Phoenix",
      client: "Property owner",
      challenge: "Owner needed a record drawing of final grading, paving, and storm utilities to support a future expansion permit submittal.",
      outcome: "Site as-built drafted from field shots tied to original control. Storm rim/invert table verified, ADA path-of-travel slopes plotted, package accepted by city on first review.",
      metrics: ["Permit-ready", "ADA verified", "Storm invert table"],
    },
  ],

  "floodplain-elevation-certificates": [
    {
      title: "Post-construction EC batch · Tampa Bay",
      client: "Coastal home builder",
      challenge: "16 new-build single-family ECs needed within 72 hours of the city's final-CO inspection deadline.",
      outcome: "Crew uploaded photos and field shots from a single FTP; our team drafted all 16 ECs against current FIRM panels, returning sealed PDFs in 60 hours.",
      metrics: ["16 ECs / 60 hr", "Current FIRM panels", "0 returns"],
    },
    {
      title: "LOMA package · Hillsborough County, FL",
      client: "Homeowner via licensed surveyor",
      challenge: "Single-family home graded above the SFHA but tagged Zone AE on the FIRM panel — needed a complete LOMA package for FEMA submittal.",
      outcome: "Drafted the LOMA exhibit map, prepared the MT-EZ form, and bundled supporting photos. FEMA issued the LOMA without an RFI.",
      metrics: ["LOMA · no RFI", "MT-EZ submittal", "Zone AE → X"],
    },
  ],

  "right-of-way": [
    {
      title: "FDOT corridor widening · I-4 segment",
      client: "Florida DOT consultant",
      challenge: "9 parcel-acquisition exhibits along a state-highway corridor, each requiring centerline-of-survey alignment, station/offset call-outs, and FDOT cell-library compliance.",
      outcome: "All 9 exhibits drafted to FDOT CADD standard, peer-reviewed by our PSM lead, and delivered with closure reports & legal descriptions ready for ROW acquisition.",
      metrics: ["9 parcels", "FDOT CADD standard", "PSM-reviewed"],
    },
    {
      title: "Utility ROW · 12 mi transmission line",
      client: "Investor-owned utility",
      challenge: "Easement exhibits for 38 adjoining parcels on a new 230 kV transmission line. Multiple title companies, varying deed bases.",
      outcome: "Standardised exhibit template, deed-research log per parcel, all 38 sealed exhibits delivered over 5 weeks. Acquisition team closed 35 of 38 with no exhibit revisions.",
      metrics: ["38 exhibits", "12 mi corridor", "92% no-revision close"],
    },
  ],

  "gis-mapping": [
    {
      title: "Asset GIS migration · municipal water utility",
      client: "City public-works dept.",
      challenge: "30 years of paper as-builts + CAD layers needed to be converted to a feature-class structure aligned with the city's enterprise GIS schema.",
      outcome: "Vectorised paper records, cleaned CAD, mapped attributes to the enterprise domain. 12,400 features published to the city's GeoDatabase with a QA report.",
      metrics: ["12,400 features", "Schema-aligned", "QA report included"],
    },
    {
      title: "Parcel basemap · regional county GIS",
      client: "County GIS coordinator",
      challenge: "Stale parcel layer mis-aligned with COGO from recent recorded plats — needed COGO-driven re-build for ~3,200 parcels.",
      outcome: "Rebuilt the parcel fabric from recorded plats and deeds using COGO traverse, achieving sub-foot alignment to control. Delivered shapefile + attribute report.",
      metrics: ["3,200 parcels", "COGO-rebuilt", "Sub-ft alignment"],
    },
  ],

  "site-plans": [
    {
      title: "Drive-thru QSR site plan · suburban Toronto",
      client: "QSR franchisee (Canada)",
      challenge: "Tight 0.6-ac corner lot, drive-thru circulation conflict, municipal zoning required 3 separate variance call-outs on the plan.",
      outcome: "Compliant site plan with stacking-lane diagram, ADA path-of-travel, and variance notes; passed planning-committee review without resubmittal.",
      metrics: ["0.6 ac", "3 variances noted", "1st-pass approval"],
    },
    {
      title: "Multi-tenant retail · Orlando, FL",
      client: "Commercial developer",
      challenge: "10-tenant retail centre site plan needed for ARB submittal with parking-count compliance and accessible-route diagrams.",
      outcome: "Plan delivered with stall-by-stall counts, signage zones, ADA routes, and FFE call-outs. ARB approved with minor landscaping comments only.",
      metrics: ["10 tenants", "Parking compliant", "ARB-approved"],
    },
  ],

  "3d-modeling": [
    {
      title: "Highway corridor model · Western US",
      client: "Civil 3D-shop survey firm",
      challenge: "Convert 4 mi of survey-grade topo into a Civil 3D corridor with assemblies, profiles, and parametric cross-sections for a pavement-rehab design.",
      outcome: "Built EG surface, corridor with 2 assemblies, profile views every 50 ft, and a clean XML deliverable that the designer used unchanged for 60% drawings.",
      metrics: ["4 mi corridor", "2 assemblies", "60% design unchanged"],
    },
    {
      title: "BIM-ready terrain · campus expansion",
      client: "Architecture / engineering JV",
      challenge: "Architect needed an IFC-exportable terrain model coordinated with the LiDAR-derived DTM for a 24-acre university expansion.",
      outcome: "Surface drafted in Civil 3D, exported to IFC with metadata for Revit linking. Architect linked directly into the federated model without re-meshing.",
      metrics: ["24 ac DTM", "IFC export", "Revit-linked"],
    },
  ],
};

export function getCaseStudies(slug) {
  return (CASE_STUDIES[slug] || []).map((c, i) => ({ ...c, slug: caseSlug(c.title, i) }));
}

/** Convert a case-study title to a URL-safe slug. */
export function caseSlug(title, idx = 0) {
  const base = title
    .toLowerCase()
    .replace(/[·•—–]/g, "-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
  return base || `case-${idx + 1}`;
}

/** Lookup one case study by service slug + case slug. */
export function getCaseStudy(serviceSlug, caseSlugValue) {
  return getCaseStudies(serviceSlug).find((c) => c.slug === caseSlugValue) || null;
}
